import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import { Calendar, Clock, Users, Phone, Mail, MapPin, ChevronRight, LogIn, LayoutDashboard, AlertCircle, XCircle, UserCog, User, LogOut, KeyRound } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { useEmbedMode, appendEmbedParam } from "@/hooks/useEmbedMode";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getLoginUrl } from "@/const";
import { hasRole, hasAnyRole } from "@/lib/roles";
import { TenantLogo } from "@/components/TenantLogo";
import { useFeatureEnabled } from "@/components/FeatureGuard";

const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const WEEKDAYS = [1, 2, 3, 4, 5]; // Monday to Friday

const PAYMENT_LABELS: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  selbstzahler: { label: "Selbstzahler", variant: "outline" },
  krankenkasse: { label: "Krankenkasse", variant: "default" },
  erstattung: { label: "Erstattung möglich", variant: "secondary" },
};

export default function Home() {
  const { user, loading: authLoading } = useAuth();
  const { data: courses, isLoading } = trpc.course.list.useQuery();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const [accessError, setAccessError] = useState<string | null>(null);
  const isEmbedded = useEmbedMode();
  
  // Feature flags
  const { enabled: selfServiceEnabled } = useFeatureEnabled("selfServiceEnabled");

  // Check for access denied error in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const message = params.get("message");
    
    if (error === "access_denied") {
      if (message === "not_registered") {
        setAccessError("Ihr Konto ist nicht registriert. Bitte kontaktieren Sie den Administrator, um Zugang zu erhalten.");
      } else if (message === "account_disabled") {
        setAccessError("Ihr Konto wurde deaktiviert. Bitte kontaktieren Sie den Administrator.");
      } else {
        setAccessError("Zugriff verweigert. Bitte kontaktieren Sie den Administrator.");
      }
      // Clean up URL
      window.history.replaceState({}, document.title, "/");
    }
  }, []);

  // Group courses by day
  const coursesByDay = WEEKDAYS.reduce((acc, day) => {
    acc[day] = courses?.filter(c => c.dayOfWeek === day) || [];
    return acc;
  }, {} as Record<number, typeof courses>);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header - Hidden when embedded in iframe */}
      {!isEmbedded && (
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-16 items-center justify-between">
            <Link href={appendEmbedParam("/")} className="flex items-center gap-3">
              <TenantLogo className="h-12 w-auto" />
            </Link>
            <nav className="flex items-center gap-4">
              <Link href={appendEmbedParam("/calendar")}>
                <Button variant="ghost" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  <span className="hidden sm:inline">Kalender</span>
                </Button>
              </Link>
              {selfServiceEnabled && (
                <Link href={appendEmbedParam("/self-service/login")}>
                  <Button variant="ghost" className="gap-2">
                    <UserCog className="h-4 w-4" />
                    <span className="hidden sm:inline">Meine Bereich</span>
                  </Button>
                </Link>
              )}
              {authLoading ? (
                <Skeleton className="h-9 w-24" />
              ) : user ? (
                <>
                  {hasAnyRole(user.roles, ["admin", "courseadmin"]) && (
                    <Link href={appendEmbedParam("/admin")}>
                      <Button className="gap-2 bg-primary hover:bg-primary/90">
                        <LayoutDashboard className="h-4 w-4" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  {hasRole(user.roles, "instructor") && !hasAnyRole(user.roles, ["admin", "courseadmin"]) && (
                    <Link href={appendEmbedParam("/admin/my-courses")}>
                      <Button className="gap-2 bg-primary hover:bg-primary/90">
                        <Users className="h-4 w-4" />
                        Meine Termine
                      </Button>
                    </Link>
                  )}
                  <UserMenu user={user} />
                </>
              ) : (
                <a href={getLoginUrl(config?.authProvider)}>
                  <Button variant="outline" className="gap-2">
                    <LogIn className="h-4 w-4" />
                    <span className="hidden sm:inline">Anmelden</span>
                  </Button>
                </a>
              )}
            </nav>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1">
        <div className="container py-8">
          {/* Hero Section */}
          <div className="mb-12 text-center">
            <h1 className="mb-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Kursübersicht
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Entdecken Sie unser vielfältiges Kursangebot für Ihre Gesundheit und Ihr Wohlbefinden. Von Rückbildungsgymnastik bis Yoga – wir haben den passenden Kurs für Sie.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link href={appendEmbedParam("/calendar")}>
                <Button size="lg" className="gap-2">
                  <Calendar className="h-5 w-5" />
                  Zum Kalender
                </Button>
              </Link>
              {config?.gymEnabled && (
                <Link href={appendEmbedParam("/gym")}>
                  <Button size="lg" className="gap-2">
                    <Users className="h-5 w-5" />
                    Fitnessstudio
                  </Button>
                </Link>
              )}
              <Button size="lg" variant="outline" className="gap-2" onClick={() => {
                const element = document.getElementById("courses");
                element?.scrollIntoView({ behavior: "smooth" });
              }}>
                Kurse ansehen
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Access Error Alert */}
          {accessError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Zugriff verweigert</AlertTitle>
              <AlertDescription>{accessError}</AlertDescription>
            </Alert>
          )}

          {/* Courses by Day */}
          {isLoading ? (
            <div className="space-y-8">
              {WEEKDAYS.map(day => (
                <section key={day}>
                  <Skeleton className="mb-4 h-8 w-32" />
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-48" />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div id="courses" className="space-y-8">
              {WEEKDAYS.map(day => {
                const dayCourses = coursesByDay[day];
                if (!dayCourses || dayCourses.length === 0) return null;

                return (
                  <section key={day}>
                    <h2 className="mb-4 text-2xl font-semibold text-foreground">
                      {DAYS_DE[day]}
                    </h2>
                    {dayCourses.length === 0 ? (
                      <Card>
                        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
                          <XCircle className="h-5 w-5" />
                          Keine Kurse an diesem Tag
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {dayCourses.map(course => (
                          <Link key={course.id} href={appendEmbedParam(`/course/${course.id}`)}>
                            <Card className="h-full cursor-pointer border-l-4 border-l-primary transition-all hover:shadow-lg hover:border-l-secondary">
                              <CardHeader className="pb-3">
                                <div className="flex flex-col gap-2">
                                  <CardTitle className="text-lg leading-tight">
                                    {course.name}
                                  </CardTitle>
                                  {PAYMENT_LABELS[course.paymentType] && (
                                    <Badge {...PAYMENT_LABELS[course.paymentType]} className="shrink-0 w-fit">
                                      {PAYMENT_LABELS[course.paymentType].label}
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription className="flex items-center gap-2 text-base">
                                  <Clock className="h-4 w-4" />
                                  {course.startTime} - {course.endTime} Uhr
                                </CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  {course.instructor && (
                                    <p className="flex items-center gap-2">
                                      <Users className="h-4 w-4" />
                                      {course.instructor.name}
                                    </p>
                                  )}
                                  {course.category && (
                                    <Badge variant="outline" className="mt-2">
                                      {course.category}
                                    </Badge>
                                  )}
                                </div>
                                <Button variant="link" className="mt-3 h-auto p-0 text-primary">
                                  Details ansehen
                                  <ChevronRight className="ml-1 h-4 w-4" />
                                </Button>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Contact Section - Hidden when embedded */}
      {!isEmbedded && (
      <section className="border-t bg-muted/50 py-12">
        <div className="container">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-foreground">Kontakt</h3>
              <div className="space-y-3 text-muted-foreground">
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {config?.contact.address.street}, {config?.contact.address.zip} {config?.contact.address.city}
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" />
                  {config?.contact.phone}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  {config?.contact.email}
                </p>
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-lg font-semibold text-foreground">Öffnungszeiten</h3>
              <div className="space-y-2 text-muted-foreground">
                {config?.contact.openingHours?.map((hours: string, index: number) => (
                  <p key={index} className={index === config.contact.openingHours!.length - 1 ? "text-sm" : ""}>{hours}</p>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-lg font-semibold text-foreground">Über uns</h3>
              <p className="text-muted-foreground">
                {config?.contact.aboutText}
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer - Hidden when embedded */}
      {!isEmbedded && (
      <footer className="border-t py-6">
        <div className="container">
          {/* Copyright and legal links */}
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {config?.tenantName}. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-4 text-sm text-muted-foreground">
              {config?.legal.imprintUrl && (
                <a href={config.legal.imprintUrl} className="hover:text-primary" target="_blank" rel="noopener noreferrer">
                  Impressum
                </a>
              )}
              {config?.legal.privacyPolicyUrl && (
                <a href={config.legal.privacyPolicyUrl} className="hover:text-primary" target="_blank" rel="noopener noreferrer">
                  Datenschutz
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}

function UserMenu({ user }: { user: any }) {
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const { logout, loading: logoutLoading } = useAuth();
  
  const passwordChangeMutation = trpc.auth.getPasswordChangeUrl.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Password change error:', error);
      alert('Passwortänderung konnte nicht initialisiert werden. Bitte versuchen Sie es später erneut.');
    }
  });

  const handleChangePassword = () => {
    if (config?.authProvider !== 'auth0') {
      return;
    }
    passwordChangeMutation.mutate();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2">
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">
            {user.name?.split(" ")[0] || "Profil"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            {user.email && (
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {config?.authProvider === 'auth0' && (
          <DropdownMenuItem
            onClick={handleChangePassword}
            disabled={passwordChangeMutation.isPending}
            className="cursor-pointer"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            {passwordChangeMutation.isPending ? "Wird geladen..." : "Passwort ändern"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={logout}
          disabled={logoutLoading}
          className="cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {logoutLoading ? "Abmelden..." : "Abmelden"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
